/**
 * Session-only campaign-detail UI state shared between brand and admin.
 *
 * Wave 2a split. Not persisted — resets on page reload.
 * Per-creator drafts → useCampaignDraftsStore.
 * Per-creator editor flags → useCampaignEditorStore.
 * Role identity + render props → useCampaignCapabilitiesStore.
 */
import { create } from 'zustand';
import type { CampaignPipeline } from '@discovr/types';

export type DeepAnalysisTab = 'overview' | 'detailed' | 'brand-safety' | 'creator-fit';

export interface CommercialPopoverRequest {
  entryId: string;
  selectedMainIds: string[];
  selectedAddonIds: string[];
}

/**
 * Minimum shape required to open the creator insights drawer.
 * Matches the common subset guaranteed by ALL V2 roster entry types.
 *
 * `creator` is admin-only (absent on brand responses).
 * `snapshot_metrics` is present on Creators/Finalized/Rejected;
 * absent on Script/Content/Live — use optional access.
 * `estimated_budget` is present on Creators/Finalized; absent elsewhere.
 */
export interface RosterRowRef {
  id: string;
  platform: {
    username: string;
    name: string;
    profile_picture_url: string;
    is_verified: boolean;
  };
  creator?: {
    name: string;
    email?: string;
  };
  snapshot_metrics?: {
    followers: number;
    avg_views: number;
    engagement_rate: number;
  };
  estimated_budget?: number;
  [key: string]: unknown;
}

export interface PriceEdit {
  creatorId: string;
  field: 'commercial' | 'final_amount';
  value: string;
}

export interface CampaignUiState {
  // ── Route identity ──────────────────────────────────────────────────────
  /** Resolved Mongo ObjectID — set by page once useResolvedCampaignId settles. */
  campaignId: string;
  /** URL slug (SSE matching, cascade resets). */
  campaignSlug: string;
  /** Stored short-link campaign code from the campaign response's `short_code`
   *  (collision-adjusted). Empty for legacy campaigns without one — callers
   *  fall back to deriving it from the slug. */
  campaignShortCode: string;
  /** Campaign platform — set once the campaign header query resolves.
   *  Used by platform-aware UI (deliverable pickers) outside the campaign data query scope. */
  campaignPlatform: string | null;
  /** Production stages the campaign runs — set once the campaign resolves.
   *  null means every stage is enabled. */
  campaignPipeline: CampaignPipeline | null;

  // ── Tab ─────────────────────────────────────────────────────────────────
  activeTab: string;

  // ── Filters ──────────────────────────────────────────────────────────────
  searchQuery: string;
  statusFilter: string;
  creatorsPage: number;

  // ── Share (page-kind mint payload) ──────────────────────────────────────
  /** Live pageSize/totalPages of whichever roster tab is on screen, published
   *  by that tab (via usePublishSharePageInfo) so the admin-header page-kind
   *  ShareButton — which has no table state of its own — can fold them into
   *  the mint payload. `null` when no roster tab is mounted (e.g. Details,
   *  Reporting). Never read for the URL itself, only for minting. */
  sharePageSize: number | null;
  shareTotalPages: number | null;
  /** RESOLVED reporting-window selection (Task 12) — '1w'|'1m'|'6m'|'full' —
   *  published by whichever windowed reporting sub-tab (General Insights,
   *  Sentiment) is on screen, via usePublishShareWindow. This is the
   *  RESOLVED value (falls back to Full Campaign when `?win` is
   *  absent from the URL — the common case for a freshly-opened tab), not
   *  the raw URL param, so the admin-header page-kind ShareButton — which
   *  has no direct access to either tab's local state — mints a grant that
   *  matches what's actually rendered on screen. `null` when no windowed
   *  reporting sub-tab is mounted. */
  shareWindow: string | null;

  // ── Modal flags ──────────────────────────────────────────────────────────
  viewingSheet: boolean;

  // ── Selection ────────────────────────────────────────────────────────────
  deepAnalysisCreator: RosterRowRef | null;
  deepAnalysisTab: DeepAnalysisTab;
  convCreator: RosterRowRef | null;
  convText: string;
  priceEdit: PriceEdit | null;
  // Cross-component approval signal: the insights drawer (Brand Safety tab) sets
  // this to ask the mounted CreatorsTab to open its approval dialog for a creator.
  approvalRequest: RosterRowRef | null;
  // Cross-component reject signal: mirror of approvalRequest — the insights drawer
  // header's Reject button sets this to ask CreatorsTab to open its reject dialog.
  rejectRequest: RosterRowRef | null;
  // Cross-component resumed deliverable selection signal: set by the resumed
  // save_deliverables action, read by CreatorsTab to reopen CommercialPopover
  // pre-filled with the selection the visitor made before signing in.
  commercialPopoverRequest: CommercialPopoverRequest | null;

  // ── Actions ──────────────────────────────────────────────────────────────
  setCampaignId: (id: string) => void;
  setCampaignSlug: (slug: string) => void;
  setCampaignShortCode: (shortCode: string) => void;
  setCampaignPlatform: (platform: string | null) => void;
  setCampaignPipeline: (pipeline: CampaignPipeline | null) => void;
  setActiveTab: (tab: string) => void;
  setSearchQuery: (q: string) => void;
  setStatusFilter: (s: string) => void;
  setCreatorsPage: (n: number) => void;
  setSharePageInfo: (pageSize: number | null, totalPages: number | null) => void;
  setShareWindow: (window: string | null) => void;
  setViewingSheet: (v: boolean) => void;
  setDeepAnalysisCreator: (c: RosterRowRef | null) => void;
  setDeepAnalysisTab: (t: DeepAnalysisTab) => void;
  setConvCreator: (c: RosterRowRef | null) => void;
  setConvText: (s: string) => void;
  setPriceEdit: (edit: PriceEdit | null) => void;
  setApprovalRequest: (c: RosterRowRef | null) => void;
  setRejectRequest: (c: RosterRowRef | null) => void;
  setCommercialPopoverRequest: (v: CommercialPopoverRequest | null) => void;
  resetUi: () => void;
}

const initialUi = {
  campaignId: '',
  campaignSlug: '',
  campaignShortCode: '',
  campaignPlatform: null as string | null,
  campaignPipeline: null as CampaignPipeline | null,
  activeTab: 'creators',
  searchQuery: '',
  statusFilter: 'all',
  creatorsPage: 0,
  sharePageSize: null as number | null,
  shareTotalPages: null as number | null,
  shareWindow: null as string | null,
  viewingSheet: false,
  deepAnalysisCreator: null,
  deepAnalysisTab: 'overview' as DeepAnalysisTab,
  convCreator: null,
  convText: '',
  priceEdit: null,
  approvalRequest: null,
  rejectRequest: null,
  commercialPopoverRequest: null,
} satisfies Omit<CampaignUiState, `set${string}` | 'resetUi'>;

export const useCampaignUiStore = create<CampaignUiState>((set) => ({
  ...initialUi,
  setCampaignId: (id) => set({ campaignId: id }),
  setCampaignSlug: (slug) => set({ campaignSlug: slug }),
  setCampaignShortCode: (shortCode) => set({ campaignShortCode: shortCode }),
  setCampaignPlatform: (platform) => set({ campaignPlatform: platform }),
  setCampaignPipeline: (pipeline) => set({ campaignPipeline: pipeline }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  setSearchQuery: (q) => set({ searchQuery: q }),
  setStatusFilter: (s) => set({ statusFilter: s }),
  setCreatorsPage: (n) => set({ creatorsPage: n }),
  setSharePageInfo: (pageSize, totalPages) => set({ sharePageSize: pageSize, shareTotalPages: totalPages }),
  setShareWindow: (window) => set({ shareWindow: window }),
  setViewingSheet: (v) => set({ viewingSheet: v }),
  setDeepAnalysisCreator: (c) => set({ deepAnalysisCreator: c }),
  setDeepAnalysisTab: (t) => set({ deepAnalysisTab: t }),
  setConvCreator: (c) => set({ convCreator: c }),
  setConvText: (s) => set({ convText: s }),
  setPriceEdit: (edit) => set({ priceEdit: edit }),
  setApprovalRequest: (c) => set({ approvalRequest: c }),
  setRejectRequest: (c) => set({ rejectRequest: c }),
  setCommercialPopoverRequest: (v) => set({ commercialPopoverRequest: v }),
  resetUi: () => set(initialUi),
}));
